import { useCallback, useEffect, useRef, useState } from "react";
import { ActualAuctionStatusEnum } from "../../../_common/enum/actualAuctionStatus";

const TURN_ALARM_MAX_MS = 15000;
const turnAlarmSrc = require("../../../_assets/iphone.mp3") as string;

export function useAnnualTurnStartAlarm(params: {
    myCatalogueUserId: number;
    currentTurnUserId: number;
    annualTimerStatus: ActualAuctionStatusEnum;
    connected: boolean;
    isRegistered: boolean;
    /** True while status arrived but annual turn state is still refetching. */
    turnSyncPending: boolean;
}) {
    const [isPlaying, setIsPlaying] = useState(false);
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const stopTimerRef = useRef<number | null>(null);
    const skippedRef = useRef(false);
    const prevRef = useRef<{ turnUserId: number; status: ActualAuctionStatusEnum } | null>(null);
    const readyRef = useRef(false);

    useEffect(() => {
        const audio = new Audio(turnAlarmSrc);
        audio.preload = "auto";
        audioRef.current = audio;
        return () => {
            if (stopTimerRef.current !== null) window.clearTimeout(stopTimerRef.current);
            audio.pause();
        };
    }, []);

    const haltPlayback = useCallback(() => {
        if (stopTimerRef.current !== null) {
            window.clearTimeout(stopTimerRef.current);
            stopTimerRef.current = null;
        }
        const audio = audioRef.current;
        if (audio) {
            audio.loop = false;
            audio.pause();
            audio.currentTime = 0;
        }
        setIsPlaying(false);
    }, []);

    const stopAlarm = useCallback(() => {
        skippedRef.current = true;
        haltPlayback();
    }, [haltPlayback]);

    useEffect(() => {
        const myId = params.myCatalogueUserId;
        const turnUserId = params.currentTurnUserId;
        const status = params.annualTimerStatus;

        // Status SignalR arrives before getAnnualState; without this the previous turn user
        // briefly still looks like currentTurnUserId + Active and the alarm flashes.
        if (params.turnSyncPending) return;

        const isMyActiveTurn =
            params.connected &&
            params.isRegistered &&
            myId > 0 &&
            myId === turnUserId &&
            status === ActualAuctionStatusEnum.Active;

        if (!readyRef.current) {
            readyRef.current = true;
            prevRef.current = { turnUserId, status };
            return;
        }

        const prev = prevRef.current ?? { turnUserId: 0, status: ActualAuctionStatusEnum.NotStarted };
        const wasMyActiveTurn = prev.turnUserId === myId && prev.status === ActualAuctionStatusEnum.Active;

        if (!isMyActiveTurn) {
            skippedRef.current = false;
            haltPlayback();
            prevRef.current = { turnUserId, status };
            return;
        }

        const resumedAfterPause = prev.status === ActualAuctionStatusEnum.Paused && prev.turnUserId === myId;
        const turnJustStarted = !wasMyActiveTurn && !resumedAfterPause && !skippedRef.current;

        if (turnJustStarted) {
            const audio = audioRef.current;
            if (audio) {
                if (stopTimerRef.current !== null) window.clearTimeout(stopTimerRef.current);
                audio.loop = true;
                audio.currentTime = 0;
                audio.play().catch(() => {});
                setIsPlaying(true);
                stopTimerRef.current = window.setTimeout(haltPlayback, TURN_ALARM_MAX_MS);
            }
        }

        prevRef.current = { turnUserId, status };
    }, [
        params.myCatalogueUserId,
        params.currentTurnUserId,
        params.annualTimerStatus,
        params.connected,
        params.isRegistered,
        params.turnSyncPending,
        haltPlayback,
    ]);

    return { isPlaying, stopAlarm };
}
